import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Award, Download, FileText, Clock, ShieldCheck } from "lucide-react";

const BACKEND = BACKEND_URL;

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso?.slice(0, 10); }
}

export default function CPD() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState("all");

  const load = (y) => {
    api.get("/cpd/me", { params: { year: y === "all" ? undefined : y } }).then(({ data }) => setData(data));
  };
  useEffect(() => { load(year); }, [year]);

  const dl = (path) => window.open(`${BACKEND}/api${path}`, "_blank");

  return (
    <div className="fade-in">
      <PageHeader title="CPD record" subtitle="Your verified continuing professional development, ready for an employer or auditor.">
        <Button variant="outline" onClick={() => dl(`/cpd/me/export.csv${year !== "all" ? `?year=${year}` : ""}`)} data-testid="cpd-export-csv">
          <Download className="mr-2 h-4 w-4" /> CSV
        </Button>
        <Button className="btn-brand" onClick={() => dl(`/cpd/me/export.pdf${year !== "all" ? `?year=${year}` : ""}`)} data-testid="cpd-export-pdf">
          <FileText className="mr-2 h-4 w-4" /> Export PDF
        </Button>
      </PageHeader>

      {/* Summary */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="card-surface p-5">
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Total CPD hours</span><Clock className="h-5 w-5 text-[#33B6E4]" /></div>
          <div className="tnum mt-2 font-heading text-[28px] font-semibold">{data?.total_hours ?? "-"}</div>
        </div>
        <div className="card-surface p-5">
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Courses completed</span><Award className="h-5 w-5 text-muted-foreground" /></div>
          <div className="tnum mt-2 font-heading text-[28px] font-semibold">{data?.count ?? "-"}</div>
        </div>
        <div className="card-surface flex flex-col justify-between p-5">
          <span className="text-sm text-muted-foreground">Filter by year</span>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="mt-2" data-testid="cpd-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All years</SelectItem>
              {(data?.years || []).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data ? null : data.completions.length === 0 ? (
        <EmptyState icon={Award} title="No completions yet"
          description="Complete a course and pass its assessment to earn CPD hours and a certificate." />
      ) : (
        <div className="card-surface overflow-hidden" data-testid="cpd-record-table">
          <div className="hidden grid-cols-[1fr_100px_90px_140px_1fr_120px] gap-3 border-b border-border bg-secondary/50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
            <div>Course</div><div>CPD hrs</div><div>Score</div><div>Completed</div><div>Reference</div><div className="text-right">Certificate</div>
          </div>
          <div className="divide-y divide-border">
            {data.completions.map((c) => (
              <div key={c.id} className="grid grid-cols-2 gap-2 px-5 py-4 sm:grid-cols-[1fr_100px_90px_140px_1fr_120px] sm:items-center" data-testid={`cpd-row-${c.id}`}>
                <div className="col-span-2 font-medium sm:col-span-1">{c.course_title}</div>
                <div className="tnum text-sm"><span className="text-muted-foreground sm:hidden">Hours: </span>{c.cpd_hours}</div>
                <div className="tnum text-sm"><span className="text-muted-foreground sm:hidden">Score: </span>{c.score}%</div>
                <div className="text-sm text-muted-foreground">{fmtDate(c.completed_at)}</div>
                <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-[#33B6E4]" /> {c.verification_ref}</div>
                <div className="col-span-2 sm:col-span-1 sm:text-right">
                  <Button size="sm" variant="outline" onClick={() => dl(`/certificates/${c.id}.pdf`)} data-testid={`download-cert-${c.id}`}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> Certificate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
